import React, { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { apiFetch } from '../../api/apiClient';
import WorkspaceTabs from '../../components/ui/WorkspaceTabs';
import SparklineChart from '../../components/ui/SparklineChart';
import { CRYPTO_TABS } from './cryptoTabs';

const POPULAR_CRYPTO = ['BTC-USD', 'ETH-USD', 'BNB-USD', 'SOL-USD', 'XRP-USD', 'DOGE-USD'];
const TAPE_CRYPTO    = ['BTC-USD', 'ETH-USD', 'BNB-USD', 'SOL-USD', 'XRP-USD', 'DOGE-USD', 'ADA-USD', 'AVAX-USD'];

const SIZE_OPTIONS = [
  { key: 50,  label: 'Top 50' },
  { key: 100, label: 'Top 100' },
  { key: 200, label: 'Top 200' },
];

function fmt(n, digits = 2) {
  if (n == null || isNaN(n)) return '—';
  return n.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function fmtPrice(n) {
  if (n == null || isNaN(n)) return '—';
  if (n >= 1000) return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (n >= 1)    return fmt(n);
  return n.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 6 });
}

function displayTicker(ticker) {
  return ticker.replace('-USD', '');
}

// ── Ticker Tape ──────────────────────────────────────────────────────────────

function TickerTapeItem({ ticker, quote }) {
  const price = quote?.currentPrice ?? null;
  const prev  = quote?.previousClose ?? null;
  const pct   = (price != null && prev != null && prev !== 0) ? ((price - prev) / prev) * 100 : 0;
  const pctColor = pct >= 0 ? '#22c55e' : '#f87171';
  const label = displayTicker(ticker);

  return (
    <div style={ts.item}>
      <span style={ts.label}>{label}</span>
      <span style={ts.price}>${fmtPrice(price)}</span>
      <span style={{ ...ts.pct, color: pctColor }}>
        {pct >= 0 ? '+' : ''}{fmt(pct)}%
      </span>
      <span style={ts.sep}>|</span>
    </div>
  );
}

const ts = {
  item: { display: 'flex', alignItems: 'center', gap: '6px', padding: '0 16px', height: '40px', flexShrink: 0 },
  label: { fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)', whiteSpace: 'nowrap' },
  price: { fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap' },
  pct:   { fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap' },
  sep:   { fontSize: '12px', color: 'var(--border)', marginLeft: '8px', userSelect: 'none' },
};

// ── Watchlist Card ────────────────────────────────────────────────────────────

function WatchlistCard({ ticker, quote, sparkPoints, onClick }) {
  const pct = (quote?.currentPrice && quote?.previousClose)
    ? ((quote.currentPrice - quote.previousClose) / quote.previousClose) * 100
    : null;
  const pctColor = pct == null ? 'var(--text-secondary)' : pct >= 0 ? '#22c55e' : '#f87171';
  const sparkColor = (sparkPoints?.length >= 2)
    ? (sparkPoints[sparkPoints.length - 1].close ?? 0) >= (sparkPoints[0].close ?? 0) ? '#22c55e' : '#f87171'
    : '#22c55e';

  const [imgErr, setImgErr] = useState(false);
  const display = displayTicker(ticker);

  return (
    <button style={wc.card} onClick={onClick} title={quote?.shortName || display}>
      <div style={wc.top}>
        <div style={wc.logoWrap}>
          {!imgErr
            ? <img
                src={`https://assets.parqet.com/logos/symbol/${display}?format=jpg`}
                alt={display}
                style={wc.logo}
                onError={() => setImgErr(true)}
              />
            : <div style={wc.logoFallback}>{display[0]}</div>
          }
        </div>
        <div style={wc.nameBlock}>
          <span style={wc.ticker}>{display}</span>
          <span style={wc.name}>{quote?.shortName || ''}</span>
        </div>
      </div>
      <div style={wc.bottom}>
        <div style={wc.priceBlock}>
          <span style={wc.price}>{quote?.currentPrice != null ? `$${fmtPrice(quote.currentPrice)}` : '—'}</span>
          <span style={{ ...wc.pct, color: pctColor }}>
            {pct != null ? `${pct >= 0 ? '+' : ''}${fmt(pct)}%` : ''}
          </span>
        </div>
        <SparklineChart points={sparkPoints || []} color={sparkColor} width={80} height={28} />
      </div>
    </button>
  );
}

const wc = {
  card: {
    minWidth: '160px', maxWidth: '200px',
    background: 'var(--surface-strong)', border: '1px solid var(--border)', borderRadius: '14px',
    padding: '14px', cursor: 'pointer', textAlign: 'left', flexShrink: 0,
    display: 'flex', flexDirection: 'column', gap: '10px',
    transition: 'border-color 0.15s',
  },
  top: { display: 'flex', alignItems: 'center', gap: '8px' },
  logoWrap: { width: 32, height: 32, borderRadius: '8px', overflow: 'hidden', flexShrink: 0 },
  logo: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  logoFallback: {
    width: '100%', height: '100%', background: 'var(--surface)', color: 'var(--text-secondary)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 700,
  },
  nameBlock: { display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0 },
  ticker: { fontSize: '13px', fontWeight: 800, color: 'var(--text-primary)', whiteSpace: 'nowrap' },
  name: { fontSize: '11px', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '110px' },
  bottom: { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '8px' },
  priceBlock: { display: 'flex', flexDirection: 'column', gap: '2px' },
  price: { fontSize: '14px', fontWeight: 800, color: 'var(--text-primary)' },
  pct:   { fontSize: '12px', fontWeight: 700 },
};

// ── Skeleton ──────────────────────────────────────────────────────────────────

function SkeletonRow() {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '60px 1fr 80px 80px', alignItems: 'center', gap: '10px', padding: '7px 8px' }}>
      {[60, 120, 60, 60].map((w, i) => (
        <div key={i} style={{ height: 12, borderRadius: 4, background: 'var(--border)', width: w, opacity: 0.5 }} />
      ))}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function CryptoOverviewPage() {
  const navigate = useNavigate();
  const { universe, quotes, loading, refresh } = useOutletContext();

  const [selectedSize, setSelectedSize] = useState(100);
  const [sparkData, setSparkData] = useState({});

  // Sparklines: fetch 7D history for popular crypto
  useEffect(() => {
    POPULAR_CRYPTO.forEach(ticker => {
      apiFetch(`/api/stock/history/${ticker}?range=7d`)
        .then(r => r.ok ? r.json() : [])
        .then(data => {
          if (Array.isArray(data) && data.length > 0)
            setSparkData(prev => ({ ...prev, [ticker]: data }));
        })
        .catch(() => {});
    });
  }, []);

  const filteredUniverse = universe.slice(0, selectedSize);

  const movers = (() => {
    const withChange = filteredUniverse
      .map(item => {
        const q = quotes[item.ticker];
        if (!q?.currentPrice || !q?.previousClose || q.previousClose <= 0) return null;
        const pct = ((q.currentPrice - q.previousClose) / q.previousClose) * 100;
        // Sanity filter: daily moves >500% are almost always bad Yahoo Finance data
        if (!isFinite(pct) || Math.abs(pct) > 500) return null;
        return { ...item, quote: q, pct };
      })
      .filter(Boolean);
    return {
      gainers: [...withChange].sort((a, b) => b.pct - a.pct).slice(0, 7),
      losers:  [...withChange].sort((a, b) => a.pct - b.pct).slice(0, 7),
    };
  })();

  return (
    <div style={s.page}>
      {/* CSS for ticker tape animation */}
      <style>{`
        @keyframes inverow-crypto-ticker-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .inverow-crypto-ticker-inner {
          display: flex;
          width: max-content;
          animation: inverow-crypto-ticker-scroll 30s linear infinite;
          gap: 0;
        }
        .inverow-crypto-ticker-inner:hover { animation-play-state: paused; }
      `}</style>

      {/* Header */}
      <div style={s.header}>
        <h1 style={s.title}>Crypto</h1>
        <button style={s.refreshBtn} onClick={refresh} title="Refresh prices">&#8635; Refresh</button>
      </div>

      <WorkspaceTabs tabs={CRYPTO_TABS} />

      {/* Universe selector */}
      <div style={s.pillRow}>
        {SIZE_OPTIONS.map(({ key, label }) => (
          <button
            key={key}
            style={{ ...s.pill, ...(selectedSize === key ? s.pillActive : {}) }}
            onClick={() => setSelectedSize(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Ticker Tape — quotes already in context */}
      <div style={s.tapeWrap}>
        <div className="inverow-crypto-ticker-inner">
          {[...TAPE_CRYPTO, ...TAPE_CRYPTO].map((ticker, i) => (
            <TickerTapeItem key={i} ticker={ticker} quote={quotes[ticker]} />
          ))}
        </div>
      </div>

      {/* Top Movers */}
      <div style={s.sectionHeader}>
        <h2 style={s.sectionTitle}>Top Movers</h2>
        <span style={s.sectionMeta}>Top {selectedSize} · daily change</span>
      </div>
      <div style={s.moversRow}>
        <MoversPanel title="Top Gainers" items={movers.gainers} color="#22c55e" loading={loading} onSelect={t => navigate(`/crypto/${t}`)} />
        <MoversPanel title="Top Losers"  items={movers.losers}  color="#f87171" loading={loading} onSelect={t => navigate(`/crypto/${t}`)} />
      </div>

      {/* Popular Watchlist */}
      <div style={{ ...s.sectionHeader, marginTop: '32px' }}>
        <h2 style={s.sectionTitle}>Popular</h2>
      </div>
      <div style={s.watchlistRow}>
        {POPULAR_CRYPTO.map(ticker => (
          <WatchlistCard
            key={ticker}
            ticker={ticker}
            quote={quotes[ticker]}
            sparkPoints={sparkData[ticker] || []}
            onClick={() => navigate(`/crypto/${ticker}`)}
          />
        ))}
      </div>

      {/* Under-development notice — remove once workspace is considered done */}
      <div style={s.devBanner}>
        🚧 Crypto workspace is under active development — more data and features coming soon.
      </div>
    </div>
  );
}

function MoversPanel({ title, items, color, loading, onSelect }) {
  return (
    <div style={s.moversCard}>
      <h3 style={{ ...s.moversTitle, color }}>{title}</h3>
      <div style={s.moversList}>
        {loading
          ? Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
          : items.map(it => (
            <button key={it.ticker} style={s.moverRow} onClick={() => onSelect(it.ticker)}>
              <span style={s.moverTicker}>{displayTicker(it.ticker)}</span>
              <span style={s.moverName}>{it.quote?.shortName || it.name}</span>
              <span style={s.moverPrice}>${fmtPrice(it.quote?.currentPrice)}</span>
              <span style={{ ...s.moverPct, color: it.pct >= 0 ? '#22c55e' : '#f87171' }}>
                {it.pct >= 0 ? '+' : ''}{fmt(it.pct)}%
              </span>
            </button>
          ))
        }
      </div>
    </div>
  );
}

const s = {
  page: { padding: '0 0 48px' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', marginBottom: '20px' },
  title: { margin: 0, fontSize: '26px', fontWeight: 800, color: 'var(--text-primary)' },
  refreshBtn: {
    background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px',
    padding: '7px 14px', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', cursor: 'pointer',
  },
  pillRow: { display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' },
  pill: {
    padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
    background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-secondary)',
  },
  pillActive: {
    background: 'var(--text-primary)', color: 'var(--surface)', borderColor: 'var(--text-primary)',
  },
  tapeWrap: {
    overflow: 'hidden', background: 'var(--surface)', border: '1px solid var(--border)',
    borderRadius: '10px', marginBottom: '28px',
  },
  sectionHeader: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' },
  sectionTitle: { margin: 0, fontSize: '15px', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' },
  sectionMeta: { fontSize: '12px', color: 'var(--text-secondary)', opacity: 0.7 },
  moversRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' },
  moversCard: { background: 'var(--surface-strong)', border: '1px solid var(--border)', borderRadius: '14px', padding: '16px' },
  moversTitle: { margin: '0 0 12px', fontSize: '13px', fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase' },
  moversList: { display: 'flex', flexDirection: 'column', gap: '4px' },
  moverRow: {
    display: 'grid', gridTemplateColumns: '60px 1fr 80px 60px', alignItems: 'center', gap: '10px',
    padding: '7px 8px', background: 'transparent', border: 'none', borderRadius: '6px', cursor: 'pointer', textAlign: 'left',
  },
  moverTicker: { fontSize: '13px', fontWeight: 800, color: 'var(--text-primary)' },
  moverName:   { fontSize: '12px', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  moverPrice:  { fontSize: '12px', color: 'var(--text-secondary)', textAlign: 'right' },
  moverPct:    { fontSize: '13px', fontWeight: 700, textAlign: 'right' },
  watchlistRow: { display: 'flex', gap: '12px', overflowX: 'auto', paddingBottom: '8px' },
  devBanner: {
    marginTop: '40px', padding: '12px 16px', borderRadius: '10px',
    background: 'var(--surface)', border: '1px solid var(--border)',
    fontSize: '13px', color: 'var(--text-secondary)',
  },
};
